import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

import { BACKEND_URL, API } from '../utils/apiConfig';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  // In-flight guard: prevents double-fire of loadUser in React 18 StrictMode
  // (which double-invokes effects in dev). NOT a cache flag — once the request
  // settles, we reset it so re-mounts after logout/login work.
  const inFlightRef = React.useRef(false);

  // Set axios default auth header
  useEffect(() => {
    // Skip on Orb pages (they manage their own auth)
    const path = window.location.pathname;
    if (path === '/orb' || path === '/orb-app') {
      setLoading(false);
      return;
    }
    // Skip auto-load when returning from OAuth with session_id in hash
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Keep loading=true until loadUser settles. Double-mount in StrictMode
      // hits the inFlight guard and short-circuits — the first call drives
      // the state, so loading stays true until /auth/me returns.
      if (!inFlightRef.current) {
        inFlightRef.current = true;
        setLoading(true);
        loadUser();
      }
    } else {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, [token]);

  const loadUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      const userData = response.data;
      
      // Also fetch vehicle data
      try {
        const vehicleResp = await axios.get(`${API}/users/my-vehicle`);
        if (vehicleResp.data?.vehicle) {
          userData.vehicle = vehicleResp.data.vehicle;
        }
      } catch (e) {
        // Vehicle fetch optional
      }
      
      // Derive name from firstName/lastName for convenience
      if (!userData.name && (userData.firstName || userData.lastName)) {
        userData.name = [userData.firstName, userData.lastName].filter(Boolean).join(' ');
      }

      setUser(userData);
    } catch (error) {
      console.error('Failed to load user:', error);
      logout();
    } finally {
      setLoading(false);
      inFlightRef.current = false;
    }
  };

  const login = async (email, password, rememberMe = false) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password,
        remember_me: rememberMe
      });
      
      const { access_token } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      inFlightRef.current = true; // we drive loadUser explicitly below
      axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
      await loadUser();
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Login fehlgeschlagen'
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      
      const { access_token } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      inFlightRef.current = true; // we drive loadUser explicitly below
      axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
      await loadUser();
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Registrierung fehlgeschlagen'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    inFlightRef.current = false;
    delete axios.defaults.headers.common['Authorization'];
  };

  const updateProfile = async (updateData) => {
    try {
      const response = await axios.put(`${API}/auth/me`, updateData);
      setUser(prev => ({ ...prev, ...response.data }));
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Update fehlgeschlagen'
      };
    }
  };

  const uploadProfilePhoto = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/users/profile-photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Reload user to get updated photo
      await loadUser();
      
      return { success: true, photoUrl: response.data.profilePhoto };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Upload fehlgeschlagen'
      };
    }
  };

  const loginWithToken = async (accessToken) => {
    localStorage.setItem('token', accessToken);
    setToken(accessToken);
    loadedRef.current = false; // Allow loadUser to run again
    axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    await loadUser();
    return { success: true };
  };

  // Login via 6-digit PIN (after magic-link onboarding completed)
  const pinLogin = async (email, pin) => {
    try {
      const r = await axios.post(`${API}/auth/pin-login`, { email, pin });
      const tk = r.data?.access_token;
      if (!tk) return { success: false, error: 'no_token' };
      return await loginWithToken(tk);
    } catch (e) {
      const detail = e.response?.data?.detail || 'invalid_credentials';
      return { success: false, error: detail };
    }
  };


  const value = {
    user,
    setUser,
    token,
    loading,
    login,
    loginWithToken,
    pinLogin,
    register,
    logout,
    updateProfile,
    uploadProfilePhoto,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};